import {SPACING, NODE_DIMENSIONS, CONNECTION_DIMENSIONS} from "./constants";

/**
 * Calculate touchpoint width/height
 *
 * Width is a static value of {NODE_WIDTH}
 * ElementHeight is based on the {node.total} relative percentage compared to the {maxValue} with a minimum value of {TOUCHPOINT_MIN_HEIGHT}
 * ConnectionHeight is calculated the same as elementHeight, only without having a minimum value
 *
 * @param {Number} maxAmount Max amount of any node within the tree
 * @param {JourneyBuilder.Sankey.SankeyNode} node
 * @returns {{width: Number, elementHeight: Number, connectionHeight: Number}}
 */
function calculateNodeDimensions(node, maxAmount) {
    //Height is calculated on what percentage our node total is of the max total
    // const valuePercentageOfTotal = node.total / maxAmount;
    // const actualHeight = Math.round(valuePercentageOfTotal * TOUCHPOINT_MAX_HEIGHT);
    const valuePercentageOfTotal = (Math.round((node.total / (maxAmount / 100))) / 100);
    const calculatedHeight = NODE_DIMENSIONS.MAX_HEIGHT * valuePercentageOfTotal;

    return {
        //Width is static
        width: NODE_DIMENSIONS.WIDTH,

        connectionHeight: Math.max(calculatedHeight, CONNECTION_DIMENSIONS.MIN_HEIGHT),
        elementHeight: Math.max(calculatedHeight, NODE_DIMENSIONS.MIN_HEIGHT)
    }
}

/**
 * Create a map between type -> max accumulated height for any level
 *
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Number} maxAmount Max amount of any node within the tree
 * @return {Object.<String, Number>}
 */
function calculateTypeHeights(tree, maxAmount) {
    /** @type {Object.<String, Object.<Number, Number>>} */
    const heightPerTypePerLevel = {};
    
    tree.forAllBranches(({node, level}) => {
        //Skip goal nodes - goal nodes have their own flow
        if(node.isGoal) {
            return;
        }
        
        const type = node.type;

        const {elementHeight} = calculateNodeDimensions(node, maxAmount);

        if (!heightPerTypePerLevel[type]) heightPerTypePerLevel[type] = {};
        if (!heightPerTypePerLevel[type][level]) heightPerTypePerLevel[type][level] = 0;

        heightPerTypePerLevel[type][level] += elementHeight;
    });

    return Object.keys(heightPerTypePerLevel).reduce((maxAccumulatedHeightPerType, type) => {        
        maxAccumulatedHeightPerType[type] = Math.max(...Object.values(heightPerTypePerLevel[type]));
        return maxAccumulatedHeightPerType;
    }, {});
}

/**
 * Calculate the start Y positions for all types
 *
 * @param {JourneyBuilder.Sankey.Tree} tree 
 * @param {String[]} types
 * @param {Number} maxAmount
 * @return {Object<String, Number>}
 */
function calculateTypeYStartPositions(tree, types, maxAmount) {
    /** @type {Object.<String, Number>} */
    const typeYStartPositions = {};

    const maxHeightPerTypes = calculateTypeHeights(tree, maxAmount);

    //While objects do not always have the guarantee of being ordered, arrays do
    //we can calculate our 'base' y values by 'stacking' our max values based on our type order
    for(let typeIndex = 0; typeIndex < types.length; typeIndex++){
        const type = types[typeIndex];
        let baseY = 0;

        //Stack on top of our previous type
        if(typeIndex !== 0){
            const previousType = types[typeIndex - 1];

            baseY += maxHeightPerTypes[previousType];
            baseY += typeYStartPositions[previousType];
            baseY += SPACING.VERTICAL;
        }

        typeYStartPositions[type] = baseY;
    }

    return typeYStartPositions;
}

/**
 * Calculate the positioning/dimension info of the given tree nodes
 * 
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Object<String, Number>} typeYStartPositions
 * @param {Number} maxAmount
 * @returns {Map<String, JourneyBuilder.Sankey.PositioningInfo>}
 */
function calculatePositionInfoPerNode(tree, typeYStartPositions, maxAmount){
    /** @type {Object.<String, Number>} */
    const yPositionsPerTypePerLevel = {};

    /**  @type {Map<String, JourneyBuilder.Sankey.PositioningInfo>} */
    const positionsPerNode = new Map();
    
    //Goal nodes always get stacked/positioned at the far end
    const goalX = tree.totalLevels * SPACING.HORIZONTAL;
    
    /** @type {Object<String, JourneyBuilder.Sankey.SankeyNode[]>} */
    const goalNodesPerBrick = {};
    
    tree.forAllBranches(({node, level}) => {
        //Goal nodes use a separate calculation; register and skip
        if(node.isGoal) {
            goalNodesPerBrick[node.brickId] = goalNodesPerBrick[node.brickId] || [];
            goalNodesPerBrick[node.brickId].push(node);
            return;
        }
        
        const typeKey = node.type;
        const typeLevelKey = `${typeKey}_${level}`;
        
        const {elementHeight, connectionHeight, width} = calculateNodeDimensions(node, maxAmount);
        
        if(!yPositionsPerTypePerLevel[typeLevelKey]){
            yPositionsPerTypePerLevel[typeLevelKey] = typeYStartPositions[typeKey];
        }

        const y = yPositionsPerTypePerLevel[typeLevelKey];
        const x = level * SPACING.HORIZONTAL;

        positionsPerNode.set(node.id, {
            x,
            y,
            elementHeight,
            connectionHeight,
            width
        });
        
        //Stack the next node of the same type/level on top of the current processed node
        yPositionsPerTypePerLevel[typeLevelKey] += elementHeight;
    });
    
    //Stack all nodes by brick with a bit of spacing between node groups
    let goalYPosition = 0;
    
    Object.values(goalNodesPerBrick)
      .forEach((nodeGroup, nodeGroupIndex) => {
          const offset = nodeGroupIndex * SPACING.VERTICAL;
          
          nodeGroup.forEach(node => {
              const {elementHeight, connectionHeight, width} = calculateNodeDimensions(node, maxAmount);
              const y = goalYPosition + offset;

              positionsPerNode.set(node.id, {
                  x: goalX,
                  y,
                  elementHeight,
                  connectionHeight,
                  width
              });

              goalYPosition += elementHeight;
          });
      });

    return positionsPerNode;
}

export {
    calculateTypeHeights,
    calculateTypeYStartPositions,
    calculatePositionInfoPerNode
}